import type { AuditLogRow, AuditSummary, Lookups, RefCollector, Summarizer, TargetKind } from "./index";
import {
  asRecord,
  bool,
  buildSummary,
  countText,
  entityLabelOf,
  fieldsFragment,
  formatCents,
  isUuid,
  num,
  plural,
  refFragment,
  resolvePerson,
  resolveTarget,
  str,
  translate,
  type Fragment,
  type MissingKind,
  type Resolution,
} from "./vocab";

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/**
 * Activity-form columns that the shared FIELD_LABELS does not cover. They are
 * mapped before `translateFields`, which passes the pt-BR text through.
 */
const ACTIVITY_FIELD_LABELS: Record<string, string> = {
  activity_type_id: "tipo",
  base_behavior: "formato",
  extra_fields: "campos extras",
  created_by: "criador",
  tenant_id: "clube",
  reminder_hours: "lembretes",
  checkin_opens_at: "janela de check-in",
  checkin_closes_at: "janela de check-in",
  requires_registration: "inscrição obrigatória",
  allow_manual_checkin: "check-in manual",
  access_control_enabled: "controle de acesso",
  visibility_class: "visibilidade",
  hard_capacity: "capacidade",
  extra_alert_staff_ids: "alertas extras",
  extra_alert_emails: "alertas extras",
  featured: "destaque",
  featured_order: "destaque",
  guest_accept_minutes: "prazo do convidado",
  members_can_see: "visível para membros",
  members_can_enroll: "inscrição de membros",
  contracted_contact_url: "contato do contratante",
  contracted_contact_enabled: "contato do contratante",
  max_guests_per_member: "convidados por membro",
  space_id: "espaço",
  location: "local",
  description_pdf: "PDF da descrição",
  youtube_url: "vídeo",
  cover_type: "tipo de capa",
};

const BASE_BEHAVIOR_LABELS: Record<string, string> = {
  internal: "no clube",
  external: "fora do clube",
  trip: "viagem",
  online: "online",
};

const CANCEL_OUTCOME_LABELS: Record<string, string> = {
  deleted: "apagada, não tinha inscritos",
};

/** `_shared/presence-lot-cancel.ts` reason union. */
const PRESENCE_LOT_REASON_LABELS: Record<string, string> = {
  sheet_closed: "fechou a tela de pagamento",
  webhook_canceled: "pagamento cancelado na Stripe",
  reaper: "pagamento expirou",
  replaced_stale_lot_invite: "trocado por um convite novo",
  replaced_own_attempt: "anfitrião refez o pagamento",
};

const RSVP_STATUS_LABELS: Record<string, string> = {
  confirmed: "confirmada",
  cancelled: "cancelada",
  waitlisted: "na fila",
  pending: "pendente",
  checked_in: "presença registrada",
};

/** `audience_rules_synced.details.entity_type` values that `resolve_audit_targets` can name. */
const AUDIENCE_TARGET_KINDS: Partial<Record<string, TargetKind>> = {
  activity: "activity",
  benefit: "benefit",
};

const LEGACY_EVENT = "Evento do modelo antigo";
const LEGACY_EXPERIENCE = "Experiência do modelo antigo";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function entityLabel(row: AuditLogRow): string {
  return entityLabelOf(row, "—");
}

function changedFields(details: unknown): Fragment {
  return fieldsFragment(details, "alterou", ACTIVITY_FIELD_LABELS);
}

function termsVersion(details: unknown): Fragment {
  const v = num(details, "access_terms_version");
  return v === null ? null : `termos v${v}`;
}

/** A target as the primary line; an id that was never recorded falls back to the entity label. */
function targetPrimary(row: AuditLogRow, lookups: Lookups, kind: TargetKind, id: unknown): Resolution | string {
  return isUuid(id) ? resolveTarget(lookups, kind, id) : entityLabel(row);
}

/**
 * A labelled lookup fragment ("anfitrião x@"). An id that was never recorded
 * adds nothing, rather than claiming the record was removed.
 */
function labelledFragment(res: Resolution | null, prefix: string): Fragment {
  if (!res) return null;
  return refFragment(
    res,
    (t) => `${prefix} ${t}`,
    (t) => `${prefix} (${t})`,
  );
}

function personFragment(lookups: Lookups, id: unknown, prefix: string, kind: MissingKind = "profile"): Fragment {
  return isUuid(id) ? labelledFragment(resolvePerson(lookups, id, kind), prefix) : null;
}

function targetFragment(lookups: Lookups, kind: TargetKind, id: unknown, prefix: string): Fragment {
  return isUuid(id) ? labelledFragment(resolveTarget(lookups, kind, id), prefix) : null;
}

function countFragment(details: unknown, zero: string, one: string, many: string): Fragment {
  return countText(details, { zero, one, many });
}

/** The activity in `entity_id`, with fragments built from `details`. */
function activitySummary(build: (details: unknown) => readonly Fragment[]): Summarizer {
  return (row, lookups) => buildSummary(targetPrimary(row, lookups, "activity", row.entity_id), build(row.details));
}

/** A legacy-model row: fixed primary, `details` qualifiers only. */
function legacySummary(primary: string, build: (details: unknown) => readonly Fragment[] = () => []): Summarizer {
  return (row) => buildSummary(primary, build(row.details));
}

/** A row whose target table is outside KTD3: entity label plus `details` qualifiers. */
function entityOnlySummary(build: (details: unknown) => readonly Fragment[]): Summarizer {
  return (row) => buildSummary(entityLabel(row), build(row.details));
}

const collectActivityEntity: RefCollector = (row, sink) => sink.target("activity", row.entity_id);

// ---------------------------------------------------------------------------
// Summarizers
// ---------------------------------------------------------------------------

const activityCancelled: Summarizer = (row, lookups): AuditSummary => {
  const d = row.details;
  const title = str(d, "title");
  const primary = title ?? targetPrimary(row, lookups, "activity", row.entity_id);
  const outcome = str(d, "outcome");
  const reason = str(d, "reason");
  const counter = (key: string, one: string, many: string): Fragment => {
    const n = num(d, key);
    return n !== null && n > 0 ? plural(n, one, many) : null;
  };
  return buildSummary(primary, [
    outcome && outcome !== "cancelled" ? translate(CANCEL_OUTCOME_LABELS, outcome) : null,
    reason ? `motivo: ${reason}` : null,
    counter("rsvps_cancelled", "inscrição cancelada", "inscrições canceladas"),
    counter("payments_refunded", "reembolso", "reembolsos"),
    counter("refunds_pending", "reembolso pendente", "reembolsos pendentes"),
    counter("invites_cancelled", "convite cancelado", "convites cancelados"),
    counter("notifications_sent", "aviso enviado", "avisos enviados"),
  ]);
};

const audienceRulesSynced: Summarizer = (row, lookups) => {
  const d = row.details;
  const type = str(d, "entity_type");
  const kind = type ? AUDIENCE_TARGET_KINDS[type] : undefined;
  let primary: Resolution | string;
  if (kind) primary = targetPrimary(row, lookups, kind, row.entity_id);
  else if (type === "event") primary = LEGACY_EVENT;
  else if (type === "experience") primary = LEGACY_EXPERIENCE;
  else primary = type ?? entityLabel(row);
  return buildSummary(primary, [
    type === "benefit" ? "benefício" : null,
    countFragment(d, "sem regras de público", "regra de público", "regras de público"),
  ]);
};

const presenceLotCancelled: Summarizer = (row, lookups) => {
  const d = row.details;
  const reason = str(d, "reason");
  return buildSummary(targetPrimary(row, lookups, "activity", asRecord(d)?.activity_id), [
    targetFragment(lookups, "referral_invite", row.entity_id, "convidado"),
    personFragment(lookups, asRecord(d)?.host_user_id, "anfitrião"),
    reason ? translate(PRESENCE_LOT_REASON_LABELS, reason) : null,
    // The flag is the caller's request; the seats actually released are not audited (R7).
    bool(d, "seats_released") === true ? "pediu devolução das vagas" : null,
  ]);
};

/**
 * KLUB-269 U4 `activity.capacity_repaired` (system): the seat counter was
 * recomputed from the open holds. The released holds belonged to invites that
 * had already ended (cancelled, expired), so the line says that, never "sem
 * convidado". `released_hold_ids` stays out of the line; the count says enough
 * and ids mean nothing to the reader.
 */
const capacityRepaired: Summarizer = (row, lookups) => {
  const d = row.details;
  const before = num(d, "capacity_held_before");
  const after = num(d, "capacity_held_after");
  const released = num(d, "released_count");
  return buildSummary(targetPrimary(row, lookups, "activity", row.entity_id), [
    before !== null && after !== null ? `vagas seguras ${before} → ${after}` : null,
    released !== null && released > 0 ? plural(released, "vaga de convite encerrado liberada", "vagas de convites encerrados liberadas") : null,
  ]);
};

/** The activity of a staff guest action: `target_id` when the invite is for an activity. */
function inviteActivityPrimary(row: AuditLogRow, lookups: Lookups): Resolution | string {
  const d = asRecord(row.details);
  return str(d, "target_type") === "activity" && isUuid(d?.target_id)
    ? resolveTarget(lookups, "activity", d?.target_id)
    : entityLabel(row);
}

/**
 * `staff-release-guest-seat` refund outcome (KTD7): exactly the values of
 * `REFUND_OUTCOMES` in supabase/functions/staff-release-guest-seat/release.ts
 * (activity.test.ts reads that list and fails on a value without a label).
 * `none` and `refunded` are handled in code. Unknown values pass through.
 */
const RELEASE_REFUND_LABELS: Record<string, string> = {
  already_refunded: "valor do anfitrião já tinha sido devolvido",
  authorization_cancelled: "cobrança do anfitrião cancelada antes de sair do cartão",
  pendency: "valor do anfitrião ficou pendente",
  failed: "estorno recusado, ficou pendente",
  deferred: "estorno do anfitrião fica para a rotina automática",
};

function releaseRefundFragment(details: unknown): Fragment {
  const outcome = str(details, "refund_outcome");
  if (outcome === null || outcome === "none") return null;
  if (outcome === "refunded") {
    // An amount is claimed only when the row carries one.
    const cents = num(details, "refund_cents");
    return cents !== null && cents > 0 ? `devolveu ${formatCents(cents)} ao anfitrião` : "estorno ao anfitrião, valor não registrado";
  }
  return translate(RELEASE_REFUND_LABELS, outcome);
}

function staffGuestSummary(extra: (details: unknown) => readonly Fragment[]): Summarizer {
  return (row, lookups) => {
    const d = row.details;
    const hostPays = bool(d, "host_pays");
    return buildSummary(inviteActivityPrimary(row, lookups), [
      targetFragment(lookups, "referral_invite", row.entity_id, "convidado"),
      personFragment(lookups, asRecord(d)?.host_id, "anfitrião"),
      // host_pays says who pays, not that the charge went through.
      hostPays === true ? "por conta do anfitrião" : null,
      ...extra(d),
    ]);
  };
}

const collectStaffGuest: RefCollector = (row, sink) => {
  const d = asRecord(row.details);
  if (str(d, "target_type") === "activity") sink.target("activity", d?.target_id);
  sink.target("referral_invite", row.entity_id);
  sink.person(d?.host_id);
};

const rsvpDemotedToWaitlist: Summarizer = (row, lookups) => {
  const d = row.details;
  const userId = asRecord(d)?.user_id;
  const activityId = asRecord(d)?.activity_id;
  const position = num(d, "waitlist_position");
  const capacity = num(d, "capacity");
  const previous = str(d, "previous_status");
  const tail: Fragment[] = [
    position !== null ? `posição ${position} na fila` : null,
    capacity !== null ? `lotação ${capacity}` : null,
    previous ? `antes: ${translate(RSVP_STATUS_LABELS, previous)}` : null,
  ];
  if (isUuid(userId)) {
    return buildSummary(resolvePerson(lookups, userId), [targetFragment(lookups, "activity", activityId, "em"), ...tail]);
  }
  return buildSummary(targetPrimary(row, lookups, "activity", activityId), tail);
};

export const summarizers: Partial<Record<string, Summarizer>> = {
  activity_created: activitySummary((d) => {
    const behavior = str(d, "base_behavior");
    return [behavior ? translate(BASE_BEHAVIOR_LABELS, behavior) : null, termsVersion(d)];
  }),
  activity_updated: activitySummary((d) => [changedFields(d), termsVersion(d)]),
  // The row is gone and its title was never recorded, so nothing is looked up.
  activity_deleted: (row) =>
    buildSummary(isUuid(row.entity_id) ? { text: "Atividade apagada", state: "missing", id: row.entity_id } : "Atividade apagada"),
  "activity.cancelled": activityCancelled,
  activity_interests_synced: activitySummary((d) => [
    countFragment(d, "removeu todos os interesses", "interesse", "interesses"),
  ]),
  refund_policies_synced: activitySummary((d) => [
    countFragment(d, "sem reembolso", "faixa de reembolso", "faixas de reembolso"),
  ]),
  audience_rules_synced: audienceRulesSynced,

  "referral_invite.presence_lot_cancelled": presenceLotCancelled,
  "referral_invite.staff_resent": staffGuestSummary(() => []),
  "referral_invite.staff_seat_released": staffGuestSummary((d) => [releaseRefundFragment(d)]),
  "activity.capacity_repaired": capacityRepaired,
  rsvp_demoted_to_waitlist: rsvpDemotedToWaitlist,
  rsvp_updated: entityOnlySummary((d) => [changedFields(d)]),
  booking_updated: entityOnlySummary((d) => [changedFields(d)]),
  referral_updated: entityOnlySummary((d) => [changedFields(d)]),

  event_created: legacySummary(LEGACY_EVENT),
  event_updated: legacySummary(LEGACY_EVENT, (d) => [changedFields(d)]),
  event_deleted: legacySummary(LEGACY_EVENT),
  event_interests_synced: legacySummary(LEGACY_EVENT, (d) => [
    countFragment(d, "removeu todos os interesses", "interesse", "interesses"),
  ]),
  event_reminder_created: legacySummary("Lembrete de evento do modelo antigo"),
  event_reminder_deleted: legacySummary("Lembrete de evento do modelo antigo"),
  experience_created: legacySummary(LEGACY_EXPERIENCE),
  experience_updated: legacySummary(LEGACY_EXPERIENCE, (d) => [changedFields(d)]),
  experience_deleted: legacySummary(LEGACY_EXPERIENCE),
  experience_participant_updated: legacySummary("Participante de experiência do modelo antigo", (d) => [
    changedFields(d),
  ]),
};

// ---------------------------------------------------------------------------
// Ref collectors (KTD3: only profiles and RPC kinds; legacy and dropped tables emit nothing)
// ---------------------------------------------------------------------------

export const refCollectors: Partial<Record<string, RefCollector>> = {
  activity_created: collectActivityEntity,
  activity_updated: collectActivityEntity,
  activity_interests_synced: collectActivityEntity,
  refund_policies_synced: collectActivityEntity,
  "activity.cancelled": (row, sink) => {
    if (!str(row.details, "title")) sink.target("activity", row.entity_id);
  },
  audience_rules_synced: (row, sink) => {
    const type = str(row.details, "entity_type");
    const kind = type ? AUDIENCE_TARGET_KINDS[type] : undefined;
    if (kind) sink.target(kind, row.entity_id);
  },
  "referral_invite.presence_lot_cancelled": (row, sink) => {
    const d = asRecord(row.details);
    sink.target("activity", d?.activity_id);
    sink.target("referral_invite", row.entity_id);
    sink.person(d?.host_user_id);
  },
  "referral_invite.staff_resent": collectStaffGuest,
  "referral_invite.staff_seat_released": collectStaffGuest,
  "activity.capacity_repaired": collectActivityEntity,
  rsvp_demoted_to_waitlist: (row, sink) => {
    const d = asRecord(row.details);
    sink.person(d?.user_id);
    sink.target("activity", d?.activity_id);
  },
};
